import React, { useEffect, useState, useMemo } from "react";
import { styled } from "@mui/material/styles";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableRow from "@mui/material/TableRow";
import Box from "@mui/material/Box";
import Tooltip, { tooltipClasses } from "@mui/material/Tooltip";
import SyncIcon from "@mui/icons-material/Sync";
import PendingActionsIcon from "@mui/icons-material/PendingActions";
import ViewResults from "./ViewResults";
import DownloadDialog from "./DownloadDialog";
import DeleteDialog from "./DeleteDialog";
import EnhancedTableHead from "./EnhancedTableHead";
import tick from "../assests/images/tick.svg";
import failed from "../assests/images/failed.svg";
import deleteIcon from "../assests/images/delete.svg";
import result from "../assests/images/result.svg";
import download from "../assests/images/download.svg";
import useResponsiveFlags from "../hooks/useResponsiveFlags";
import FilterMenuResults from "./utilities/FilterMenuResults";
import WrapperContainer from "./utilities/WrapperContainer";
import axios from "../api/axios";

export default function Results() {
  const { isDesktop } = useResponsiveFlags();
  const [data, setData] = useState([]);
  const [order, setOrder] = useState("desc");
  const [orderBy, setOrderBy] = useState("id");
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [search, setSearch] = useState(""); // For global search
  const [filters, setFilters] = useState({
    runType: null,
    startDate: null,
    endDate: null,
    mode: null,
    normalisation: null,
  });
  const [isSidePanelOpen, setIsSidePanelOpen] = useState(false);
  const [selectedRunDetails, setSelectedRunDetails] = useState(null);
  const [showDialog, setShowDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [dataLoading, setDataLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const handleDownloadIconClick = (row) => {
    setShowDialog(true);
    setSelectedRunDetails(row.id);
  };
  const handleCloseDialog = (e) => {
    e.preventDefault();
    setShowDialog(false);
  };
  const handleDeleteIconClick = (row) => {
    setShowDeleteDialog(true);
    setSelectedRunDetails(row.id);
  };
  const handleCloseDeleteDialog = (e) => {
    e.preventDefault();
    setShowDeleteDialog(false);
  };
  const handleFileDownload = async (selectedFile) => {
    axios
      .post("/api/download-job-files", {
        jobid: selectedRunDetails,
        selectedFile: selectedFile,
      })
      .then((res) => {
        const downloadUrl = res.data.downloadUrl;
        const link = document.createElement("a");
        link.href = downloadUrl;
        link.target = "_blank";
        document.body.appendChild(link);
        link.click();
        link.remove();
      })
      .catch((err) => {
        setErrorMessage(
          err.response?.data?.message
            ? err.response.data.message
            : err.response?.data?.error
            ? err.response.data.error
            : `Failed to download the requested file`
        );
      });
    setShowDialog(false);
  };
  const handleViewResult = (row) => {
    setSelectedRunDetails(row.id);
    setIsSidePanelOpen(true);
  };
  const fetchData = async () => {
    setDataLoading(true);
    axios
      .get("/api/get-jobs")
      .then((res) => {
        setData(res.data.items.data);
        setDataLoading(false);
      })
      .catch((err) => {
        setErrorMessage("Failed to fetch jobs data.");
        setDataLoading(false);
      });
  };
  useEffect(() => {
    if (errorMessage || successMessage) {
      const timer = setTimeout(() => {
        setSuccessMessage("");
        setErrorMessage("");
      }, 10000); // 10 seconds
      return () => clearTimeout(timer);
    }
  }, [errorMessage, successMessage]);

  const handleSearchChange = (event) => {
    setSearch(event.target.value.toLowerCase());
  };

  const handleFilterChange = (column, value) => {
    setFilters((prevFilters) => ({
      ...prevFilters,
      [column]: prevFilters[column] === value ? null : value,
    }));
  };

  const filteredData = useMemo(() => {
    return data.filter((row) => {
      const searchableFields = ["runname", "runtype", "parameter"];

      const matchesSearch = searchableFields.some(field =>
        row[field]?.toString().toLowerCase().includes(search)
      ) || String(row.id).includes(search);
      
      const parsedRowDate = row.date ? new Date(row.date) : null;

      const filterStartDate = filters.startDate
        ? new Date(filters.startDate)
        : null;

      const filterEndDate = filters.endDate ? new Date(filters.endDate) : null;

      const parameters = row.parameter ? parseParameters(row.parameter) : {};

      const matchesFilters =
        (!filters.runType ||
          row.runtype?.toLowerCase() === filters.runType.toLowerCase()) &&
        (!filterStartDate || parsedRowDate >= filterStartDate) &&
        (!filterEndDate || parsedRowDate <= filterEndDate) &&
        (!filters.mode ||
          parameters["Signature Matrix"]?.toLowerCase() ===
            filters.mode.toLowerCase()) &&
        (!filters.normalisation ||
          parameters["Quantile Normalization"]?.toLowerCase() ===
            filters.normalisation.toLowerCase());
      return matchesSearch && matchesFilters;
    });
  }, [data, filters, search]);

  function parseParameters(parametersString) {
    const parameters = {};

    // Split by newlines to separate key-value pairs
    const lines = parametersString.split("\n");

    lines.forEach((line) => {
      // Split each line by ":" into key and value
      const [key, value] = line.split(":").map((item) => item.trim()); // Trim spaces around key and value

      if (key && value) {
        // Normalize both key and value to avoid issues with case and extra spaces
        parameters[key] = value;
      }
    });

    return parameters;
  }

  const BootstrapTooltip = styled(({ className, ...props }) => (
    <Tooltip {...props} arrow classes={{ popper: className }} />
  ))(({ theme }) => ({
    [`& .${tooltipClasses.arrow}`]: {
      color: theme.palette.common.black,
    },
    [`& .${tooltipClasses.tooltip}`]: {
      backgroundColor: theme.palette.common.black,
    },
  }));

  const handleSort = (property) => {
    const isAscending = orderBy === property && order === "asc";
    setOrder(isAscending ? "desc" : "asc");
    setOrderBy(property);
  };

  const sortedData = useMemo(() => {
    return [...filteredData].sort(getComparator(order, orderBy));
  }, [filteredData, order, orderBy]);

  function getComparator(order, orderBy) {
    return order === "desc"
      ? (a, b) => descendingComparator(a, b, orderBy)
      : (a, b) => -descendingComparator(a, b, orderBy);
  }

  function descendingComparator(a, b, orderBy) {
    let valueA = a[orderBy];
    let valueB = b[orderBy];

    if (orderBy === "date") {
      valueA = valueA ? new Date(valueA) : null;
      valueB = valueB ? new Date(valueB) : null;
    }

    if (orderBy === "id") {
      valueA = Number(valueA);
      valueB = Number(valueB);
    }

    if (typeof valueA === "string") valueA = valueA.toLowerCase();
    if (typeof valueB === "string") valueB = valueB.toLowerCase();

    if (valueA == null) return 1;
    if (valueB == null) return -1;

    if (valueB < valueA) return -1;
    if (valueB > valueA) return 1;
    return 0;
  }

  const handleDelete = async () => {
    axios
      .post("/api/delete-job-files", {
        jobid: selectedRunDetails,
      })
      .then((res) => {
        setSuccessMessage(res.data.message);
        fetchData();
      })
      .catch((err) => {
        setErrorMessage(
          err.response?.data?.message
            ? err.response.data.message
            : err.response?.data?.error
            ? err.response.data.error
            : `Failed to delete the requested job file`
        );
        fetchData();
      });
    setShowDeleteDialog(false);
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    const days = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
    const dayName = days[date.getDay()];
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    const year = String(date.getFullYear()).slice(-2);
    return `${dayName} ${month}/${day}/${year}`;
  };

  return (
    <>
      <WrapperContainer
        title="Results"
        loggedin={true}
        content={
          <>
            <div
              className="d-flex justify-content-between"
              style={{ paddingTop: "16px" }}
            >
              <div className="d-flex align-items-center mb-3">
                <input
                  type="text"
                  className="form-control search-icon"
                  placeholder="Search"
                  // value={searchWord}
                  onChange={handleSearchChange}
                />
                <FilterMenuResults
                  handleFilterChange={handleFilterChange}
                  filters={filters}
                />
              </div>
            </div>
            {errorMessage && (
              <div id="error-message" className="error-message">
                {errorMessage}
              </div>
            )}
            {successMessage && (
              <div id="success-message" className="success-message">
                {successMessage}
              </div>
            )}
            <div style={{ margin: isDesktop ? "0 -0.8rem" : "" }}>
              <TableContainer>
                <Table stickyHeader>
                  <EnhancedTableHead
                    order={order}
                    orderBy={orderBy}
                    onSort={handleSort}
                  />
                  <TableBody>
                    {dataLoading ? (
                      <TableRow>
                        <TableCell colSpan={8} align="center">
                          Loading...
                        </TableCell>
                      </TableRow>
                    ) : sortedData.length > 0 ? (
                      sortedData.map((row) => (
                        <TableRow key={row.id}>
                          <TableCell
                            style={{
                              position: "sticky",
                              left: 0,
                              backgroundColor: "#fff",
                              zIndex: 1,
                            }}
                            className="custom-size"
                          >
                            {row.runname}
                          </TableCell>
                          <TableCell className="custom-size">
                            {row.id}
                          </TableCell>
                          <TableCell className="custom-size">
                            {row.runtype}
                          </TableCell>
                          <TableCell className="custom-size">
                            {formatDate(row.date)}
                          </TableCell>
                          <TableCell className="custom-size" style={{ whiteSpace: "pre-line" }}>
                            {row.parameter}
                          </TableCell>
                          <TableCell className="custom-size text-center">
                            {row.size}
                          </TableCell>
                          <TableCell>
                            <Box
                              display="flex"
                              justifyContent="center"
                              alignItems="center"
                            >
                              {row.status === 0 && (
                                <BootstrapTooltip title="Run Pending">
                                  <PendingActionsIcon />
                                </BootstrapTooltip>
                              )}
                              {row.status === 1 && (
                                <BootstrapTooltip title="Run Success">
                                  <img src={tick} alt="Run Success" />
                                </BootstrapTooltip>
                              )}
                              {row.status === 2 && (
                                <BootstrapTooltip title="Run Failed">
                                  <img src={failed} alt="Run Failed" />
                                </BootstrapTooltip>
                              )}
                              {row.status === 3 && (
                                <BootstrapTooltip title="Currently Running">
                                  <SyncIcon />
                                </BootstrapTooltip>
                              )}
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Box
                              display="flex"
                              justifyContent="center"
                              alignItems="center"
                            >
                              <BootstrapTooltip title="View Result">
                                <span>
                                  <button
                                    className="results-btn btn btn-sm"
                                    onClick={() => handleViewResult(row)}
                                  >
                                    <img src={result} alt="" />
                                  </button>
                                </span>
                              </BootstrapTooltip>
                              <BootstrapTooltip title="Download">
                                <span>
                                  <button
                                    className="results-btn btn btn-sm"
                                    onClick={() => handleDownloadIconClick(row)}
                                    disabled={row.buttonDisabled}
                                  >
                                    <img src={download} alt="" />
                                  </button>
                                </span>
                              </BootstrapTooltip>
                              <BootstrapTooltip title="Delete">
                                <span>
                                  <button
                                    className="results-btn btn btn-sm"
                                    onClick={() => handleDeleteIconClick(row)}
                                    disabled={row.buttonDisabled}
                                  >
                                    <img src={deleteIcon} alt="" />
                                  </button>
                                </span>
                              </BootstrapTooltip>
                            </Box>
                          </TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={8} align="center">
                          No matching records found.
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </div>
            {isSidePanelOpen && (
              <ViewResults
                isOpen={isSidePanelOpen}
                onClose={() => setIsSidePanelOpen(false)}
                jobId={selectedRunDetails}
              />
            )}
            {showDialog && (
              <DownloadDialog
                show={showDialog}
                onClose={handleCloseDialog}
                onDownload={handleFileDownload}
                jobId={selectedRunDetails}
              />
            )}

            {showDeleteDialog && (
              <DeleteDialog
                show={showDeleteDialog}
                onClose={handleCloseDeleteDialog}
                onDelete={handleDelete}
              />
            )}
          </>
        }
      />
    </>
  );
}
